import { useEffect, useMemo, useState } from 'react';
import { RawCell, cellText as rawCellText, isPureNumericText, isNoteColumn } from 'app/utils/tableCells';
import { cellKey } from './aiResultGrouping';

export const MAX_AI_CELLS = 500; // matches the backend TOO_MANY_CELLS cap

interface UseAiCellPickerArgs {
  show: boolean;
  grid: RawCell[][];
  tableTitle?: string;
  refDataName?: string;
  reset: () => void;
}

type GridCell = { value?: string | null; format?: string; background?: string };

const asCell = (cell: RawCell): GridCell | undefined => (cell != null && typeof cell === 'object' ? cell : undefined);

/**
 * Owns the AI modal's "pick cells" step: grid text helpers, the default-included
 * numeric-cell selection, click-to-toggle (cell / row / header span), the sheet
 * render data, and the request-cell list. Text helpers (textOf/rowTextOf/colTextOf)
 * and isThead are also used by the results view, so they're re-exported.
 * Extracted from AiApplicationModal.
 */
export function useAiCellPicker({ show, grid, tableTitle, refDataName, reset }: UseAiCellPickerArgs) {
  const isThead = (r: number, c: number): boolean => asCell(grid[r]?.[c])?.format === 'thead';

  const rowTextOf = (row: number): string => rawCellText(grid[row]?.[0]);
  const colTextOf = (row: number, col: number): string => {
    const colParts: string[] = [];
    for (let r = 0; r < row; r++) {
      let c = col;
      while (c >= 1 && !rawCellText(grid[r]?.[c])) c--;
      if (c < 1) continue;
      if (r !== 0 && !isThead(r, c)) continue;
      colParts.push(rawCellText(grid[r]?.[c]));
    }
    return colParts.join(' ');
  };
  const textOf = (row: number, col: number): string => [rowTextOf(row), colTextOf(row, col)].filter(Boolean).join(' — ');
  const isIncludable = (r: number, c: number): boolean => r >= 1 && c >= 1 && !isThead(r, c) && isPureNumericText(rawCellText(grid[r]?.[c]));

  const [included, setIncluded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!show) return;
    reset();
    const next = new Set<string>();
    const noteCols = new Map<number, boolean>();
    for (let r = 1; r < grid.length; r++) {
      for (let c = 1; c < (grid[r]?.length ?? 0); c++) {
        if (!isIncludable(r, c)) continue;
        if (!noteCols.has(c)) noteCols.set(c, isNoteColumn(grid, c));
        if (!noteCols.get(c)) next.add(cellKey(r, c));
      }
    }
    setIncluded(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, grid]);

  const toggleCells = (targets: { r: number; c: number }[]) => {
    const includable = targets.filter(({ r, c }) => isIncludable(r, c));
    if (!includable.length) return;
    setIncluded((prev) => {
      const allIn = includable.every(({ r, c }) => prev.has(cellKey(r, c)));
      const next = new Set(prev);
      includable.forEach(({ r, c }) => (allIn ? next.delete(cellKey(r, c)) : next.add(cellKey(r, c))));
      return next;
    });
  };

  const headerSpan = (r: number, c: number): number[] => {
    const rowLen = grid[r]?.length ?? 0;
    const cols = [c];
    for (let c2 = c + 1; c2 < rowLen && rawCellText(grid[r]?.[c2]) === ''; c2++) cols.push(c2);
    return cols;
  };

  const allCellsInRow = (r: number) => Array.from({ length: grid[r]?.length ?? 0 }, (_, c) => ({ r, c }));
  const allCellsInCols = (cols: number[]) => cols.flatMap((c) => grid.map((_, r) => ({ r, c })));

  const handlePickClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const cellEl = (e.target as HTMLElement).closest?.('td, th') as HTMLTableCellElement | null;
    if (!cellEl) return;
    const rowEl = cellEl.parentElement as HTMLTableRowElement | null;
    if (!rowEl) return;
    const r = rowEl.rowIndex;
    const c = cellEl.cellIndex;
    if (r === 0 && c === 0) return;
    if (c === 0) return toggleCells(allCellsInRow(r)); // row label → toggle row
    if (r === 0 || isThead(r, c)) return toggleCells(allCellsInCols(headerSpan(r, c))); // header → toggle spanned column(s)
    toggleCells([{ r, c }]); // data cell → toggle itself
  };

  const sheetData = useMemo(
    () =>
      grid.map((row, r) =>
        (row ?? []).map((cell, c) => {
          const base: GridCell = asCell(cell) ?? { value: typeof cell === 'string' ? cell : '' };
          const background = [base.background, included.has(cellKey(r, c)) ? 'ai-pick-on' : null].filter(Boolean).join(' ');
          return { ...base, readOnly: true, background };
        })
      ),
    [grid, included]
  );

  const titleToSend = (tableTitle?.trim() || rawCellText(grid[0]?.[0])).slice(0, 200);
  const displayTitle = titleToSend || refDataName || '';

  const requestCells = useMemo(() => {
    const cells: { row: number; col: number }[] = [];
    for (let r = 1; r < grid.length; r++) {
      for (let c = 1; c < (grid[r]?.length ?? 0); c++) {
        if (included.has(cellKey(r, c))) cells.push({ row: r, col: c });
      }
    }
    return cells;
  }, [grid, included]);
  const cappedLocally = requestCells.length > MAX_AI_CELLS;

  return { included, sheetData, requestCells, cappedLocally, handlePickClick, titleToSend, displayTitle, textOf, rowTextOf, colTextOf, isThead };
}
